import { Fragment, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { entityLink } from "../utils/entityRoute";
import { AronRefLink } from "./AronRefLink";

const CHIP =
  "inline-flex items-center rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium " +
  "text-red-700 no-underline align-baseline";

// Minimal hast (rehype) node shape — enough to walk a GFM table and re-render
// its cells ourselves for the responsive (table ⇆ stacked-card) layout.
interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  properties?: { href?: string };
  children?: HastNode[];
}

// Ścieżki, które istnieją tylko w maszynie ARON-a. Zgłoszenie właściciela
// 2026-08-25: odpowiedź zawierała `sandbox:/mnt/data/mapa_eop96_bogaczewo.png`,
// przeglądarka wyrenderowała z tego odnośnik prowadzący na pustą stronę. Backend
// podmienia takie adresy na prawdziwe (apps/aron/services/file_links.py), ale
// stare wiadomości zostały w bazie — tu odbieramy im klikalność.
const MACHINE_PATH = /^(?:sandbox:|computer:|file:|\/mnt\/|\/work|\/tmp\/|\/root\/|[a-zA-Z]:[\\/])/;

export function isMachinePath(href?: string): boolean {
  return MACHINE_PATH.test((href ?? "").trim());
}

// Trwały znacznik pliku wydanego przez ARON-a. Backend podmienia go na świeży,
// podpisany adres S3 przy każdym odczycie wiadomości (apps/aron/services/
// file_links.resolve_text) — do przeglądarki znacznik dociera TYLKO wtedy, gdy
// pliku nie da się już wydać: skasowany albo wstrzymany do czasu skanowania.
// Wtedy nazwa zostaje, a klikalność nie — link prowadziłby w pustą stronę.
export function isUnresolvedFileMarker(href?: string): boolean {
  return (href ?? "").trim().startsWith("aron-file:");
}

/** Render an anchor, mapping the custom `aron-ref:` scheme to an in-app chip/link. */
function Anchor({
  href,
  conversationId,
  children,
}: {
  href?: string;
  conversationId?: string | null;
  children: ReactNode;
}) {
  if (href?.startsWith("aron-ref:")) {
    const parts = href.split(":");
    const entity = parts[1] ?? "";
    const id = parts[2] ?? "";
    // Strona obiektu, a gdy jej nie ma — moduł, w którym obiekt żyje. Bez tego
    // drugiego poziomu połowa typów renderowała się jako nieklikalna plakietka:
    // ARON pisał „utworzyłem zgłoszenie / opłatę / notatkę", a kliknąć nie było gdzie.
    const to = entityLink(entity, id)?.route ?? null;
    if (to) {
      return (
        <AronRefLink to={to} conversationId={conversationId} className={CHIP} title={`${entity} ${id}`}>
          {children}
        </AronRefLink>
      );
    }
    return (
      <span className={CHIP} title={`${entity} ${id}`}>
        {children}
      </span>
    );
  }
  if (isUnresolvedFileMarker(href)) {
    return (
      <code
        className="rounded bg-black/5 px-1"
        title="Plik nie jest już dostępny do pobrania (usunięty albo wstrzymany do czasu skanowania)"
      >
        {children}
      </code>
    );
  }
  if (isMachinePath(href)) {
    // Nie odnośnik, tylko ścieżka wewnętrzna — kliknięcie prowadziłoby donikąd.
    return (
      <code
        className="rounded bg-black/5 px-1"
        title="Ścieżka wewnętrzna — plik nie został udostępniony do pobrania"
      >
        {children}
      </code>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className="text-red-600 underline">
      {children}
    </a>
  );
}

/** Re-render the inline content of a table cell from its hast children, keeping
 * the markup ARON actually emits inside cells (links/chips, bold, code). */
function renderInline(nodes: HastNode[] | undefined, conversationId: string | null | undefined, keyPrefix: string): ReactNode {
  if (!nodes) return null;
  return nodes.map((n, i) => {
    const key = `${keyPrefix}.${i}`;
    if (n.type === "text") return <Fragment key={key}>{n.value}</Fragment>;
    if (n.type !== "element") return null;
    const kids = renderInline(n.children, conversationId, key);
    switch (n.tagName) {
      case "a":
        return (
          <Anchor key={key} href={n.properties?.href} conversationId={conversationId}>
            {kids}
          </Anchor>
        );
      case "strong":
      case "b":
        return <strong key={key}>{kids}</strong>;
      case "em":
      case "i":
        return <em key={key}>{kids}</em>;
      case "code":
        return (
          <code key={key} className="rounded bg-black/5 px-1">
            {kids}
          </code>
        );
      case "br":
        return <br key={key} />;
      default:
        return <Fragment key={key}>{kids}</Fragment>;
    }
  });
}

const el = (n: HastNode | undefined, tag: string): HastNode | undefined =>
  n?.children?.find((c) => c.type === "element" && c.tagName === tag);

const els = (n: HastNode | undefined, tags: string[]): HastNode[] =>
  (n?.children ?? []).filter((c) => c.type === "element" && tags.includes(c.tagName ?? ""));

/** Responsive table: a real bordered table on ≥sm, a stack of label:value cards on
 * mobile, where each header label is paired with its cell value. Avoids the two
 * bad mobile outcomes — crammed multi-line columns, or a clipped sideways scroll. */
function AronTable({ node, conversationId }: { node: HastNode; conversationId?: string | null }) {
  const headerRow = els(el(node, "thead"), ["tr"])[0];
  const headers = els(headerRow, ["th", "td"]);
  const bodyRows = els(el(node, "tbody"), ["tr"]).map((tr) => els(tr, ["td", "th"]));

  return (
    <div className="my-2">
      {/* Desktop — standard table */}
      <div className="hidden max-w-full overflow-x-auto rounded-lg border border-gray-200 sm:block dark:border-gray-700">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-800/60">
            <tr className="border-b border-gray-100 dark:border-gray-700">
              {headers.map((h, i) => (
                <th key={i} className="px-2 py-1.5 text-left align-top font-semibold">
                  {renderInline(h.children, conversationId, `dh${i}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((cells, ri) => (
              <tr key={ri} className="border-b border-gray-100 last:border-0 dark:border-gray-700">
                {cells.map((c, ci) => (
                  <td key={ci} className="px-2 py-1 align-top">
                    {renderInline(c.children, conversationId, `dr${ri}.${ci}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile — one card per row, header label paired with each value */}
      <div className="flex flex-col gap-2 sm:hidden">
        {bodyRows.map((cells, ri) => (
          <div
            key={ri}
            className="overflow-hidden rounded-lg border border-gray-200 text-xs dark:border-gray-700"
          >
            {cells.map((c, ci) => (
              <div
                key={ci}
                className="flex items-start justify-between gap-3 border-b border-gray-100 px-2.5 py-1.5 last:border-0 odd:bg-gray-50/60 dark:border-gray-700/60 dark:odd:bg-gray-800/30"
              >
                <span className="shrink-0 font-medium text-gray-500 dark:text-gray-400">
                  {renderInline(headers[ci]?.children, conversationId, `mh${ri}.${ci}`)}
                </span>
                <span className="min-w-0 break-words text-right">
                  {renderInline(c.children, conversationId, `mr${ri}.${ci}`)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AronMarkdown({
  text,
  conversationId,
}: {
  text: string;
  conversationId?: string | null;
}) {
  return (
    <div className="aron-md text-sm leading-relaxed break-words [&_p]:my-1 [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_code]:break-words [&_code]:rounded [&_code]:bg-black/5 [&_code]:px-1">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // Keep custom aron-ref: scheme intact (default transform would strip it).
        urlTransform={(url) => url}
        components={{
          // Custom responsive renderer (table on desktop, cards on mobile).
          table({ node }) {
            return <AronTable node={node as unknown as HastNode} conversationId={conversationId} />;
          },
          a({ href, children }) {
            return (
              <Anchor href={href} conversationId={conversationId}>
                {children}
              </Anchor>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
